import { isEmpty, isNil } from 'lodash'

import {
  COMPLETED_EXPERIMENT_STATUSES,
  DISMISSED_EXPERIMENT_STATUS,
  EMPTY_STATE,
  INSIGHT_TYPE,
  LAUNCHED_EXPERIMENT_STATUSES,
  REASON_TYPE,
  RUNNING_EXPERIMENT_STATUSES,
  SCOPE_ALL,
} from './AgenticInsights.constants'

export const getExpectedConversionsPerWeek = (insight) =>
  insight.expectedConversionsPerWeek != null
    ? Math.max(0, Math.round(insight.expectedConversionsPerWeek))
    : 0

export const getBaselineConversionRate = (opportunityStats) => {
  if (!opportunityStats || !opportunityStats.conclusiveCalls) {
    return null
  }
  return opportunityStats.convertedCalls / opportunityStats.conclusiveCalls
}

export const getProjectedConversionRate = (opportunityStats, conversionPctLift) => {
  const baseline = getBaselineConversionRate(opportunityStats)
  if (isNil(baseline) || isNil(conversionPctLift)) {
    return null
  }
  return baseline + conversionPctLift
}

export const formatRatePercent = (rate) => (isNil(rate) ? null : `${Math.round(rate * 100)}%`)

// The run-level multiplier that projects a sample count up to the org's monthly
// call volume. 1 for runs predating the scaling change (backend sends null),
// so those fall back to showing the raw sample count.
export const getMonthlyProjectionFactor = (insight) =>
  insight?.pipelineStats?.monthlyProjectionFactor ?? 1

// The opportunity label to show a user: the friendly, goal-phrased name when
// present, else the raw analytical taxonomy name.
export const getOpportunityLabel = (insight) =>
  insight?.conversionOpportunityDisplayName || insight?.conversionOpportunity

// A monthly-count label under a conversion-rate figure, e.g. "348 / mo".
// `count` is a per-run sample count; `factor` projects it to monthly volume.
export const formatMonthlyCount = (count, factor = 1) =>
  isNil(count) ? null : `${Math.round(count * factor).toLocaleString()} / mo`

// Converted calls implied by applying a conversion rate to the opportunity's
// decided (conclusive) calls.
export const convertedCountAtRate = (opportunityStats, rate) =>
  isNil(rate) || !opportunityStats?.conclusiveCalls
    ? null
    : Math.round(opportunityStats.conclusiveCalls * rate)

// The Now → Projected ConversionPanel props for an insight. Shared by the
// insight card and the hero card so both always report the exact same figures.
export const getConversionPanelProps = (insight) => {
  const baselineRate = getBaselineConversionRate(insight.opportunityStats)
  const projectedRate = getProjectedConversionRate(
    insight.opportunityStats,
    insight.conversionPctLift
  )
  const monthlyFactor = getMonthlyProjectionFactor(insight)
  const nowCount = insight.opportunityStats?.convertedCalls ?? null
  const projectedCount = convertedCountAtRate(insight.opportunityStats, projectedRate)
  return {
    nowKey: 'Now',
    nowValue: formatRatePercent(baselineRate) || '—',
    nowSub: formatMonthlyCount(nowCount, monthlyFactor),
    projKey: 'Projected',
    projValue: formatRatePercent(projectedRate) || '—',
    projSub: formatMonthlyCount(projectedCount, monthlyFactor),
  }
}

export const isExperimentComplete = (experiment) =>
  experiment?.status === 'succeeded' ||
  experiment?.status === 'failed' ||
  experiment?.status === 'cancelled'

export const INSIGHT_TYPE_LABELS = {
  operational: 'Executive Insight',
  call_center: 'Agent Insight',
}

export const CHANNEL_CALL = 'call'
export const CHANNEL_EMAIL = 'email'
export const CHANNEL_CHAT = 'chat'
export const CHANNEL_SMS = 'sms'
export const CHANNEL_FILTER_ALL = 'all'

export const CHANNEL_LABELS = {
  [CHANNEL_CALL]: 'Call',
  [CHANNEL_EMAIL]: 'Email',
  [CHANNEL_CHAT]: 'Chat',
  [CHANNEL_SMS]: 'SMS',
}

export const MESSAGING_CHANNELS = [CHANNEL_EMAIL, CHANNEL_CHAT, CHANNEL_SMS]

export const isMessagingChannel = (channel) => MESSAGING_CHANNELS.includes(channel)

// Noun used as the base for copy on the list page (eyebrow / title) keyed by
// channel. `CHANNEL_FILTER_ALL` is only ever passed when the visible insights
// span more than one channel (the page resolves a single-channel "All" view to
// that channel, and pure-voice to 'call'), so the neutral "conversation" reads
// correctly for a genuinely mixed feed while voice-only stays "call".
const CHANNEL_FILTER_NOUN = {
  [CHANNEL_FILTER_ALL]: 'conversation',
  [CHANNEL_CALL]: 'call',
  [CHANNEL_EMAIL]: 'email',
  [CHANNEL_CHAT]: 'chat',
  [CHANNEL_SMS]: 'SMS',
}

export const getChannelFilterNoun = (channelFilter) => CHANNEL_FILTER_NOUN[channelFilter] || 'call'

// Voice copy is the original. For messaging-channel insights the same
// template just swaps "calls" for the generic "conversations".
const INSIGHT_TYPE_DESCRIPTIONS_VOICE = {
  operational:
    "Shows process or training gaps that are hurting your team's close rate. Built from calls where the loss was flagged as an internal issue.",
  call_center:
    'Shows objections where your best agents close more deals than others. Built from calls where the loss was flagged as a customer objection.',
}

const INSIGHT_TYPE_DESCRIPTIONS_MESSAGING = {
  operational:
    "Shows process or training gaps that are hurting your team's close rate. Built from conversations where the loss was flagged as an internal issue.",
  call_center:
    'Shows objections where your best agents close more deals than others. Built from conversations where the loss was flagged as a customer objection.',
}

// Kept as the voice default for any caller that doesn't have a channel in hand
// (e.g. a tooltip rendered in a channel-less list view).
export const INSIGHT_TYPE_DESCRIPTIONS = INSIGHT_TYPE_DESCRIPTIONS_VOICE

export const getInsightTypeDescription = (insightType, channel) => {
  const table = isMessagingChannel(channel)
    ? INSIGHT_TYPE_DESCRIPTIONS_MESSAGING
    : INSIGHT_TYPE_DESCRIPTIONS_VOICE
  return table[insightType]
}

// What an experiment measures, as a phrase to drop mid-sentence.
//
// "an opportunity to book an appointment" only reads correctly with the friendly
// display name, which is verb-phrased. The raw taxonomy name is a noun phrase
// ("New appointment booked") and would read as broken English there, so it gets a
// flatter frame. Runs predating the display-name work, and any where generating
// one failed, take the fallback.
export const getMeasuredCallsPhrase = (insight) =>
  insight.conversionOpportunityDisplayName
    ? `calls where there was an opportunity to ${insight.conversionOpportunityDisplayName}`
    : `calls in ${insight.conversionOpportunity}`

// Users reach the UI as {first_name, last_name, username} and are always shown as
// one display string. Falls back to the username, which is never null.
export const formatUserName = (user) =>
  [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username

export const formatInsightFromAPI = (apiInsight) => ({
  id: apiInsight.id,
  organizationId: apiInsight.organization_id,
  pipelineRunId: apiInsight.pipeline_run_id,
  insightType: apiInsight.insight_type,
  channel: apiInsight.channel || CHANNEL_CALL,
  conversionOpportunity: apiInsight.conversion_opportunity,
  conversionOpportunityDisplayName: apiInsight.conversion_opportunity_display_name,
  conversionOpportunityDescription: apiInsight.conversion_opportunity_description,
  conversionReason: apiInsight.conversion_reason,
  conversionReasonDescription: apiInsight.conversion_reason_description,
  title: apiInsight.title,
  description: apiInsight.description,
  summary: apiInsight.summary,
  recommendation: apiInsight.recommendation,
  // TODO(backend): a short, plain-language description of the recommendation,
  // generated + persisted per run. Absent today; the card falls back to the
  // (verbose) verbatim `recommendation` until this ships.
  recommendationSummary: apiInsight.recommendation_summary,
  // The real-time detection rule written into the playbook deck card at launch.
  // Null means this insight cannot be launched as live guidance.
  guidanceTriggerPrompt: apiInsight.guidance_trigger_prompt,
  pctOfConclusive: apiInsight.pct_of_conclusive,
  conversionPctLift: apiInsight.conversion_pct_lift,
  expectedConversionsPerWeek: apiInsight.expected_conversions_per_week,
  // Why an experiment can't be launched for this insight, or null if it can.
  // The launch endpoints enforce this regardless of what the UI shows.
  experimentBlockedReason: apiInsight.experiment_blocked_reason,
  ranking: apiInsight.ranking,
  createdAt: apiInsight.created_at,
  playbookCids: apiInsight.playbook_cids || [],
  // What the run was scoped to (playbook / agents / organization). Null for
  // payloads that predate run_scope; scope helpers fall back to playbookCids.
  runScope: apiInsight.run_scope
    ? {
        type: apiInsight.run_scope.type,
        playbookCids: apiInsight.run_scope.playbook_cids || [],
        userIds: apiInsight.run_scope.user_ids || [],
        label: apiInsight.run_scope.label || null,
      }
    : null,
  // Where an experiment's guidance and coaching go isn't carried on the insight:
  // working it out joins the call table per insight, which is the cost TREN-400
  // took out of the list endpoint. The drawer asks
  // GET /coaching/insight-coaching-conflicts/{id} when it opens.
  dynamicPromptCreatedAt: apiInsight.dynamic_prompt_created_at,
  experiment: apiInsight.experiment
    ? {
        status: apiInsight.experiment.status,
        startDate: apiInsight.experiment.start_date,
        daysToTrack: apiInsight.experiment.days_to_track,
        daysElapsed: apiInsight.experiment.days_elapsed,
        report: apiInsight.experiment.report,
        callsProcessed: apiInsight.experiment.calls_processed,
        conversionRate: apiInsight.experiment.conversion_rate,
        baselineCallsProcessed: apiInsight.experiment.baseline_calls_processed,
        baselineConversionRate: apiInsight.experiment.baseline_conversion_rate,
        startedByUser: apiInsight.experiment.started_by_user
          ? formatUserName(apiInsight.experiment.started_by_user)
          : null,
        agentCount: apiInsight.experiment.agent_count ?? 0,
        dailyTrend: apiInsight.experiment.daily_trend || [],
      }
    : null,
  evidence: apiInsight.evidence.map((item) => ({
    id: item.id,
    insightId: item.insight_id,
    callId: item.call_id,
    messageId: item.message_id,
    excerpt: item.excerpt,
    anchorTime: item.anchor_time,
    anchorText: item.anchor_text,
    explanation: item.explanation,
    explanationSummary: item.explanation_summary,
    createdAt: item.created_at,
  })),
  pipelineStats: apiInsight.pipeline_stats
    ? {
        totalCalls: apiInsight.pipeline_stats.total_calls,
        numOpportunities: apiInsight.pipeline_stats.num_opportunities,
        sampleStartDate: apiInsight.pipeline_stats.sample_start_date,
        sampleEndDate: apiInsight.pipeline_stats.sample_end_date,
        monthlyProjectionFactor: apiInsight.pipeline_stats.monthly_projection_factor,
        noOpportunityCalls: apiInsight.pipeline_stats.no_opportunity_calls,
      }
    : null,
  opportunityStats: apiInsight.opportunity_stats
    ? {
        totalCalls: apiInsight.opportunity_stats.total_calls,
        conclusiveCalls: apiInsight.opportunity_stats.conclusive_calls,
        convertedCalls: apiInsight.opportunity_stats.converted_calls,
        objectionCount: apiInsight.opportunity_stats.objection_count,
      }
    : null,
  weeklyTrend: apiInsight.weekly_trend || [],
  people: (apiInsight.reviewed_users || []).map(formatUserName),
})

export const formatInsightsFromAPI = (apiInsights) => {
  return apiInsights.map(formatInsightFromAPI)
}

// ---- Redesign (three-tab structure) ----

export const isAgentInsight = (insight) => insight.insightType === INSIGHT_TYPE.AGENT

export const isExecutiveInsight = (insight) => insight.insightType === INSIGHT_TYPE.EXECUTIVE

// An insight's reason is an objection (agent-addressable, on the call) or an
// operational issue (off the call). Mirrors the agent/executive split.
export const getReasonType = (insight) =>
  isAgentInsight(insight) ? REASON_TYPE.OBJECTION : REASON_TYPE.OPERATIONAL

export const getExperimentStatus = (insight) => insight.experiment?.status ?? null

export const isLaunched = (insight) =>
  LAUNCHED_EXPERIMENT_STATUSES.includes(getExperimentStatus(insight))

export const isRunningExperiment = (insight) =>
  RUNNING_EXPERIMENT_STATUSES.includes(getExperimentStatus(insight))

export const isCompletedExperiment = (insight) =>
  COMPLETED_EXPERIMENT_STATUSES.includes(getExperimentStatus(insight))

export const isDismissed = (insight) => getExperimentStatus(insight) === DISMISSED_EXPERIMENT_STATUS

// The Baseline → Measured ConversionPanel props for a launched experiment.
// Shared by the experiments card and the drawer so the two cannot report
// different figures for the same experiment. The drawer used to derive its own
// from opportunityStats and the pre-launch conversionPctLift, which is a
// projection frozen at generation time — it never moved once the experiment
// started, so a card showing a fall and a drawer showing a rise were both
// "correct" about different numbers.
//
// Requires a launched insight: it reads insight.experiment directly rather than
// guarding, because every caller has already established there is one (isLaunched
// in the drawer, an early return in the card) and a panel for an insight with no
// experiment has nothing to show anyway.
export const getExperimentPanelProps = (insight) => {
  const { experiment } = insight
  const monthlyFactor = getMonthlyProjectionFactor(insight)
  return {
    measured: true,
    nowKey: 'Baseline',
    nowValue: formatRatePercent(experiment.baselineConversionRate) || '—',
    nowSub: formatMonthlyCount(
      convertedCountAtRate(insight.opportunityStats, experiment.baselineConversionRate),
      monthlyFactor
    ),
    projKey: isRunningExperiment(insight) ? 'So far' : 'Measured',
    projValue: formatRatePercent(experiment.conversionRate) || '—',
    projSub: formatMonthlyCount(
      convertedCountAtRate(insight.opportunityStats, experiment.conversionRate),
      monthlyFactor
    ),
  }
}

// The realized change in conversion rate since launch, in whole percentage
// points. Null until both sides have a rate: an experiment whose calls haven't
// been scored yet has no result, which is not the same as a result of zero.
//
// Differences the two rates *as displayed* rather than rounding the true
// difference, so the lift always equals what a reader gets by subtracting the
// two printed percentages. Rounding the true difference is more precise but can
// contradict the panel above it — org 726's rates are 65.43% and 64.71%, which
// both print as 65% while the true -0.72pp change prints as "-1%". At
// whole-percent display precision those two rates *are* the same number, and a
// panel that disagrees with itself is worse than one that is a fraction of a
// point coarse. Revisit alongside surfacing confidence intervals, where the
// honest answer for a gap that small is "no detectable change" either way.
export const getMeasuredLiftPoints = (insight) => {
  if (isNil(insight.experiment)) {
    return null
  }
  const { baselineConversionRate, conversionRate } = insight.experiment
  if (isNil(baselineConversionRate) || isNil(conversionRate)) {
    return null
  }
  return Math.round(conversionRate * 100) - Math.round(baselineConversionRate * 100)
}

// Signed, so a decline reads as one. The old display hardcoded a leading "+",
// which turned a measured fall into "+-9%".
//
// Signs the rounded value rather than the raw one, so a decline too small to
// show — -0.3pp, say — reads "+0%" rather than "-0%", which looks like a
// rendering fault rather than a number. Same shape as the other signed deltas in
// the app (TopMoversWidget, StrengthsWeaknessesWidget): prepend the plus, let a
// negative carry its own sign.
export const formatLiftPoints = (points) => {
  if (isNil(points)) {
    return null
  }
  const rounded = Math.round(points)
  return `${rounded >= 0 ? '+' : ''}${rounded}%`
}

// The Insights tab is the "to-do" surface: agent insights with a real
// recommendation that haven't been launched yet, ranked by the conversions
// they'd recover per week.
export const getActionableInsights = (insights) =>
  insights
    .filter(
      (insight) =>
        isAgentInsight(insight) && !isEmpty(insight.recommendation) && !isLaunched(insight)
    )
    .sort(
      (insightA, insightB) =>
        getExpectedConversionsPerWeek(insightB) - getExpectedConversionsPerWeek(insightA)
    )

// Executive (operational) insights: off-call process/training fixes. Unlike
// agent insights they aren't launched as experiments, so there's no launched
// filter — just the ones carrying a recommendation, ranked by how much of the
// conclusive calls they account for (prevalence, since they have no real lift).
export const getExecutiveInsights = (insights) =>
  insights
    .filter((insight) => isExecutiveInsight(insight) && !isEmpty(insight.recommendation))
    .sort((insightA, insightB) => (insightB.pctOfConclusive ?? 0) - (insightA.pctOfConclusive ?? 0))

export const getLaunchedInsights = (insights) => insights.filter(isLaunched)

export const getRunningExperiments = (insights) => insights.filter(isRunningExperiment)

export const getCompletedExperiments = (insights) => insights.filter(isCompletedExperiment)

export const getPlaybookName = (playbooks, cid) =>
  playbooks.find((playbook) => playbook.cid === cid)?.name || cid

// A run's scope is a single thing that can combine facets: it targets some
// playbooks, some agents, both at once, or nothing (the whole org). We derive
// one scope entry per insight (its run's scope), keyed by the facet combination
// so insights from the same scope group together, with a `facets` list for the
// UI to render (chips) and a composed `name`. Falls back to playbookCids for
// payloads that predate run_scope. Returns a single-element array so callers can
// treat every insight uniformly.
export const getScopeEntries = (insight, playbooks) => {
  const scope = insight.runScope
  const cids = !isEmpty(scope?.playbookCids) ? scope.playbookCids : insight.playbookCids || []
  const userIds = scope?.userIds || []

  const facets = cids.map((cid) => ({ type: 'playbook', label: getPlaybookName(playbooks, cid) }))
  if (!isEmpty(userIds)) {
    facets.push({
      type: 'agents',
      label: `${userIds.length} ${userIds.length === 1 ? 'agent' : 'agents'}`,
    })
  }
  if (isEmpty(facets)) {
    facets.push({ type: 'organization', label: 'Whole organization' })
  }

  const keyParts = []
  if (!isEmpty(cids)) {
    keyParts.push(`pb:${[...cids].sort((cidA, cidB) => cidA.localeCompare(cidB)).join(',')}`)
  }
  if (!isEmpty(userIds)) {
    keyParts.push(`ag:${[...userIds].sort((idA, idB) => idA - idB).join(',')}`)
  }
  const key = isEmpty(keyParts) ? 'organization' : keyParts.join('|')

  const allPlaybookFacets = facets.every((facet) => facet.type === 'playbook')
  let type = 'combined'
  if (facets.length === 1) {
    type = facets[0].type
  } else if (allPlaybookFacets) {
    type = 'playbook'
  }

  return [{ key, name: facets.map((facet) => facet.label).join(' · '), type, facets }]
}

// Scope keys don't depend on playbook names, so pass no playbooks.
export const getInsightScopeKeys = (insight) =>
  getScopeEntries(insight, []).map((entry) => entry.key)

// Scope is a run's target. SCOPE_ALL spans every scope; otherwise match by key.
export const isInScope = (insight, scope) =>
  scope === SCOPE_ALL || getInsightScopeKeys(insight).includes(scope)

export const getInsightsInScope = (insights, scope) =>
  insights.filter((insight) => isInScope(insight, scope))

// Rich per-scope summary for the selector: insight counts (agent / executive),
// calls analyzed, the run date, and whether every scope is a playbook (so the
// UI keeps the "playbook" wording when that's all there is). Dismissed insights
// still list their scope — so it can say everything was dismissed — but don't
// count toward it.
export const getScopeSummary = (insights, playbooks) => {
  const byKey = new Map()
  insights.forEach((insight) => {
    getScopeEntries(insight, playbooks).forEach((entry) => {
      const current = byKey.get(entry.key) || {
        ...entry,
        agentCount: 0,
        execCount: 0,
        callCount: null,
      }
      if (!isDismissed(insight)) {
        if (insight.insightType === INSIGHT_TYPE.AGENT) {
          current.agentCount += 1
        } else if (insight.insightType === INSIGHT_TYPE.EXECUTIVE) {
          current.execCount += 1
        }
      }
      if (isNil(current.callCount) && insight.pipelineStats) {
        current.callCount = insight.pipelineStats.totalCalls
      }
      byKey.set(entry.key, current)
    })
  })
  const scopes = [...byKey.values()]
  return {
    scopes,
    scopeCount: scopes.length,
    allPlaybook: scopes.every((entry) => entry.type === 'playbook'),
    runDate: insights.find((insight) => insight.createdAt)?.createdAt ?? null,
  }
}

// Group insights by scope for the "All" view. Each insight belongs to exactly
// one scope — its run's scope (getScopeEntries returns a single combined entry
// per run) — since runs are independent and there are no cross-run totals.
export const groupInsightsByScope = (insights, playbooks) => {
  const groups = {}
  insights.forEach((insight) => {
    getScopeEntries(insight, playbooks).forEach((entry) => {
      groups[entry.name] = groups[entry.name] || []
      groups[entry.name].push(insight)
    })
  })
  return groups
}

// Break a recommendation into discrete agent-facing steps, one per sentence,
// for the dynamic prompt's checklist (each item is checked off independently as
// the agent says it — see createDynamicPromptFromInsight). Newer
// recommendations arrive as clean, period-separated sentences, so the sentence
// split alone does the work; the extra break on semicolons and em-dashes only
// affects older recommendations that packed several points into one sentence,
// which we still want surfaced as separate steps. The split only needs to be
// close — items stay editable in the playbook editor afterward.
const SENTENCE_BOUNDARY = /([.?!])\s+(?=["'‘“]?[A-Z])/g

// Uppercase the first letter of a step so a fragment split off mid-sentence at
// an em-dash/semicolon (e.g. "let's secure today's rate") still reads as its
// own line. Only skips leading quotes/parens/whitespace — a step starting with
// a number (e.g. "3.5% discount") is left alone so we don't capitalize its
// first word by mistake.
const capitalizeFirstLetter = (text) =>
  text.replace(/^(["'‘“(\s]*)([a-z])/, (match, lead, letter) => lead + letter.toUpperCase())

export const splitRecommendationIntoDisplayItems = (recommendation) => {
  if (isEmpty(recommendation)) {
    return []
  }
  return recommendation
    .trim()
    .replace(SENTENCE_BOUNDARY, '$1\n')
    .split(/\n|\s*[;—]\s*/)
    .map((step) => step.trim())
    .filter((step) => !isEmpty(step))
    .map(capitalizeFirstLetter)
}

// Best-effort client-side heuristic for the per-scope empty state. The org-level
// "has this org ever run" signal now exists (hasRuns, used by the page-level
// empty state), but the per-scope LOW_VOLUME / FIRST_RUN distinction still needs
// run-level metadata the API doesn't expose yet. Tracked in TREN-395 (meaningful
// per-run empty states off the shared breakdown computation).
//
// An empty scope isn't necessarily a first run: its insights may have aged past
// the cutoff or been dismissed, and both need their own copy rather than "your
// first analysis is on the way".
export const getEmptyStateKind = ({
  scopeInsights,
  actionableInsights,
  launchedInsights,
  hasExpiredInsights,
  hasDismissedInsights,
}) => {
  if (isEmpty(scopeInsights) && hasExpiredInsights) {
    return EMPTY_STATE.EXPIRED
  }
  if (hasDismissedInsights) {
    return EMPTY_STATE.ALL_DISMISSED
  }
  if (isEmpty(scopeInsights)) {
    return EMPTY_STATE.FIRST_RUN
  }
  if (isEmpty(actionableInsights) && !isEmpty(launchedInsights)) {
    return EMPTY_STATE.ALL_LAUNCHED
  }
  return EMPTY_STATE.NOTHING_ACTIONABLE
}
